import { EventEmitter, Injectable } from '@angular/core';
import { IpcRenderService } from './ipc-render.service';
import { StorageService } from './storage.service';
import { ImplementedOn, StorageName } from '../enum/common-enum';
import { SettingsService } from '../../components/settings/settings.service';
import { ToastrService } from 'ngx-toastr';
import { Subject } from 'rxjs';
import { Router } from '@angular/router';
import { SendDataService } from './send-data.service';
import { SettingTab } from '../../components/settings/set-software/set-software.model';

@Injectable({
  providedIn: 'root'
})
export class WeightMachineService {
  public portsList: any[] = [];
  public getAllDeviceList: any[] = [];
  storageName = StorageName;
  isPortOpen = false;
  dataLogged: EventEmitter<string> = new EventEmitter<string>();
  isMachineConnected : boolean = false;
  machineIsDisconnecting : boolean = false;
  // For Setting Weignt MAchine in Web
  private port: any | null = null;
  private textEncoder: TextEncoderStream | null = null;
  private writableStreamClosed: Promise<void> | null = null;
  private writer: WritableStreamDefaultWriter | null = null;
  private reader: ReadableStreamDefaultReader | null = null;

  constructor(
    public ipcService: IpcRenderService,
    private settingsService: SettingsService,
    private toaster: ToastrService,
    private storageService: StorageService,
    public router : Router,
    private sendDataService: SendDataService,
  ) { }


//#region For Connecting and Listening to Weight Machine in Web :
  async connect(baudRate: number = 0) {
    try {

      if (baudRate == 0) {
        const data = this.storageService.retrieve(StorageName.WEIGHT_MACHINE_SETTING_DATA);
        if (data && data.length > 0) {
          baudRate = data[0]?.baudRateName
        } else {
          this.toaster?.error('Please Configure Weight Machine Setting.');
          this.storageService.store(StorageName.SET_SOFTWARE_ACTIVE_TAB_ID,SettingTab.Devices.toString());
          this.router.navigate(['/settings/set-software']);
          this.sendDataService.navigateDeviceWeightMachine.next(true);
          return;
        }
      }
      // Check if already connected
      if (this.port && this.port.readable) {
        this.isMachineConnected = true;
        console.warn('Port is already open');
        // this?.toaster?.warning('Serial Port is already connected.');
        return;
      }

      // Request user to select a port
      this.port = await (navigator as any).serial?.requestPort();
      await this.openPort(baudRate);
    } catch (error) {
      console.error('Failed to connect to Serial Port', error);
      this?.toaster?.error('Failed to connect to Serial Port');
    }
  }

  async openPort(baudRate: number) {
    if (!this.port) return;

    await this.port.open({ baudRate });
    this.textEncoder = new TextEncoderStream();
    this.writableStreamClosed = this.textEncoder.readable.pipeTo(this.port.writable);
    this.writer = this.textEncoder.writable.getWriter();

    this.isMachineConnected  = true;
    this?.toaster?.success('Serial Connected');

    this.readData();

    // Handle device disconnection
    this.port.addEventListener('disconnect', () => {
      this.handleDisconnect();
    });

    // Handle manual port closure
    await (navigator as any).serial?.addEventListener('disconnect', (event: any) => {
      if (event.target === this.port) {
        this.disconnect();
      }
    });
  }

  async readData() {
    const textDecoder = new TextDecoderStream();
    const readableStreamClosed = this.port.readable.pipeTo(textDecoder.writable);
    this.reader = textDecoder.readable.getReader();

    try {
      while (true) {
        const { value, done } = await this.reader.read();
        if (done) break;
        if (value) {
          this.handleSerialData(value);
        }
      }
    } catch (error) {
      console.error('Error reading from Serial Port', error);
      this.handleDisconnect();
    }
  }

  async handleDisconnect() {
    this.isMachineConnected  = false;
    this?.toaster?.warning('Serial Disconnected');

    // Attempt auto-reconnect
    console.warn('Attempting to auto-reconnect...');
    await this.autoReconnect();
  }

  async autoReconnect() {
    const data = this.storageService.retrieve(StorageName.WEIGHT_MACHINE_SETTING_DATA);
        if (data) {
         var baudRate = data[0]?.baudRateName;
        }
    while (true) {
      try {
        const ports = await (navigator as any).serial.getPorts();
        if (ports.length > 0) {
          this.port = ports[0];
          await this.openPort(baudRate ? baudRate : 9600 );
          return;
        }
      } catch (error) {
        console.warn('Reconnection attempt failed... Retrying in 2 seconds');
      }
      await this.sleep(2000);
    }
  }

  async disconnect() {
    try {
      if (this.reader) {
        await this.reader.cancel();
        this.reader.releaseLock();
      }
      if (this.writer) {
        await this.writer.close();
      }
      if (this.writableStreamClosed) {
        await this.writableStreamClosed;
      }
      if (this.port) {
        await this.port.close();
        this.port = null;

        this.isMachineConnected  = false;
        this.machineIsDisconnecting = false;
        this?.toaster?.success('Serial Disconnected');
      }
    } catch (error) {
      console.error('Failed to disconnect the device', error);
    }
  }

  sleep(ms: number) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

//#endregion  For Connecting and Listening to Weight Machine in Web end.


  initializeWeightMachine(): void {
    this.setupWeightMachinePorts(undefined);
    this.settingsService.getDeviceByImplementedOnid(ImplementedOn.WeightMachine).subscribe({
      next: (res) => {
        if (!res?.isSuccess) {
          // this.handleError('Failed to retrieve devices', res?.message);
          return;
        }
  
        const weightMachineData = res?.response;
  
        if (!weightMachineData) {
          // this.toaster.error('Weight Machine Not Configured');
          return;
        }
  
        this.setupWeightMachinePorts(weightMachineData);
      },
      error: (err) => {
        // this.handleError('Device API call failed', err?.message);
      }
    });
  }
  
  private setupWeightMachinePorts(weightMachineData: any): void {
    this.ipcService.send('getPorts');

    this.ipcService.on('portsList', (event, ports) => {
      this.portsList = ports;
      console.log('Ports list:', this.portsList);
      this.storageService.store(this.storageName.PORT_DROPDOWN, ports);

      const portToOpen = this.getSavedOrDefaultPort(ports, weightMachineData);
      if (portToOpen) {
        this.openSelectedPort(portToOpen);
      }
    });

    this.ipcService.on('portOpened', () => {
      this.isPortOpen = true;
      console.log('Port successfully opened');
    });

    this.ipcService.on('portClosed', () => {
      this.isPortOpen = false;
      console.log('Port successfully closed');
    });
  }

  private handleError(defaultMessage: string, customMessage?: string): void {
    this.getAllDeviceList = [];
    this.toaster.error(customMessage || defaultMessage);
  }

  // Function to stop listening to events when destroyed
  stopListening() {
    // this.dataLogged?.unsubscribe();
    this.ipcService.removeAllListener("serialData");
    this.ipcService.removeAllListener("portOpened");
    this.ipcService.removeAllListener("openPort");
    // this.disconnectPort();
    this.ipcService.removelListener();
  }

  getSavedOrDefaultPort(portsList: any[], weightMachineData: any): any {
    console.log('savedPort name:', weightMachineData);
    let portToOpen = portsList.find(p => p.path === weightMachineData?.comPort) || portsList[0];

    // Safely assign baudRate with a default if undefined or invalid
    const baudRate = Number(weightMachineData?.baudRateName);
    portToOpen.baudRate = !isNaN(baudRate) ? baudRate : 9600;  // Default to 9600 if not a valid number

    console.log('portToOpen:', portToOpen);
    return JSON.stringify(portToOpen);
  }

  openSelectedPort(port: any) {
    if (port) {
      this.ipcService.send('openPort', port);
    }
  }

  private delimiter: string = '\n'; // Default, but we'll detect dynamically
  private serialData: string = '';

  handleSerialData(data: string) {
    console.log('Raw Serial Data:', JSON.stringify(data)); // Debugging

    this.serialData += data; // Append incoming data to buffer

    // Dynamically detect delimiter if not yet confirmed
    if (!this.delimiter || this.delimiter === '\n') {
      const detectedDelimiter = this.detectDelimiter(data);
      if (detectedDelimiter) {
        this.delimiter = detectedDelimiter;
      }
    }

    let delimiterIndex;
    while ((delimiterIndex = this.serialData.indexOf(this.delimiter)) !== -1) {
      let completeMessage = this.serialData.slice(0, delimiterIndex).trim();
      this.serialData = this.serialData.slice(delimiterIndex + this.delimiter.length); // Remove processed data

      if (completeMessage) {
        console.log('Complete Message:', completeMessage);
        const sanitized = this.sanitizeData(completeMessage);
        this.dataLogged.emit(sanitized);
        console.log('Serial Data Processed:', sanitized);
      }
    }
  }

  handleSerialDataOld(outputData: string) {
    if (outputData) {
      const sanitized = this.sanitizeData(outputData);
      this.dataLogged.emit(sanitized);
      console.log('Serial Data Processed:', sanitized);
    }
  }

// Function to detect delimiter dynamically
private detectDelimiter(data: string): string | null {
    if (data.includes('\r\n')) return '\r\n'; // Windows-style
    if (data.includes('\r')) return '\r';     // Old Mac-style
    if (data.includes('\n')) return '\n';     // Unix/Linux-style
    return null;
}

// Function to sanitize data (extracts numbers, removes unwanted chars)
private sanitizeData(data: string): string {
    return data.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1'); // Keep only numbers and one decimal
}


  disconnectPort(): void {
    console.log('disconnectPort')
    this.ipcService.send('disconnectPort');
  }

}
